/**
 * PilotOversightScreen — Risk panel > Reviews > Pilot oversight (Risk panel 0.27.0, Bill 2026-11-04).
 *
 * The pilot test workbook, as the testers work it in ROP-teams (Pilot & requests > Pilot testing), plus Risk
 * Management's own sections that testers never see: Pre-Launch Responsibilities, Control Testing, Post-Implementation.
 * The Summary (the workbook's Summary tab, counted live), every item with its status and the life-ring reports filed
 * from it, and every change: who, when, old -> new. Risk Management adds, edits and retires items and sections; each
 * change is recorded under the person signed in.
 *
 * Server: /v3/risk/pilot/* (pcb-retail-api 3.48.0; RiskManager, SuperAdmin passes; verified sign-in).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyState, FilterChips, ModalShell, PageHeader, StatusChip } from '@retail-ops/panel-shell';
import {
  addPilotItem,
  addPilotSection,
  loadItemHistory,
  loadPilotBoard,
  loadPilotHistory,
  updatePilotItem,
  updatePilotSection,
  type PilotBoard,
  type PilotHistoryRow,
  type PilotItem,
  type PilotSection,
  type PilotSectionInput,
} from '@/services/pilotOversightApi';
import { EMPTY_FILTER, diff, draftOf, filterItems, headline, historyWords, nameOf, statusTone, totals, when, type OversightFilter } from './pilotOversightModel';
import './pilotOversight.css';

type View = 'items' | 'changes';

const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

// ── item editor ──────────────────────────────────────────────────────────────────────────────────────────────

function ItemEditor({ item, section, statuses, onClose, onSaved }: {
  item: PilotItem;
  section: PilotSection | undefined;
  statuses: ReadonlyArray<string>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Record<string, string>>(() => draftOf(item));
  const [history, setHistory] = useState<PilotHistoryRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changes = diff(item, draft);
  const dirty = Object.keys(changes).length > 0;
  const label = (field: string, fallback: string): string => section?.columnLabels?.[field] || fallback;
  const scenario = section?.kind !== 'checklist';

  useEffect(() => {
    let cancelled = false;
    loadItemHistory(item.id).then((r) => { if (!cancelled) setHistory(r.history); }).catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [item.id, item.version]);

  async function save(extra: { retired?: boolean } = {}): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updatePilotItem(item.id, { ...changes, ...extra, expectedVersion: item.version || undefined });
      onSaved();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  const field = (k: string, text: string, multi = false): JSX.Element => (
    <label className={`po-field${multi ? ' po-field--wide' : ''}`}>
      <span>{text}</span>
      {multi
        ? <textarea rows={4} value={draft[k] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
        : <input value={draft[k] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />}
    </label>
  );

  return (
    <ModalShell open title={`${item.code ?? '(no ID)'} · ${headline(item, section)}`} onClose={onClose} width={760}
      actions={(
        <>
          {error && <span className="po-error" role="alert">{error}</span>}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void save({ retired: !item.retired })}>
            {item.retired ? 'Restore item' : 'Retire item'}
          </button>
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onClose}>Close</button>
          <button type="button" className="btn btn--primary" disabled={busy || !dirty} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}>
      <p className="po-muted">{section?.name}{section?.audience === 'risk' ? ' · Risk only' : ' · testers work this in ROP'}{item.retired ? ' · retired' : ''}</p>
      {item.issues.length > 0 && (
        <div className="banner banner--warn" role="note">
          Issues reported from this item: {item.issues.map((x) => x.changeKey ?? 'report sent, change number to follow').join(', ')}
        </div>
      )}
      <div className="po-form">
        <label className="po-field">
          <span>Status</span>
          <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
            <option value="">No status</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {scenario && (
          <label className="po-field">
            <span>Pass / fail</span>
            <select value={draft.result} onChange={(e) => setDraft((d) => ({ ...d, result: e.target.value }))}>
              <option value="">Not recorded</option><option value="Pass">Pass</option><option value="Fail">Fail</option>
            </select>
          </label>
        )}
        {field('itemCode', 'ID')}
        {field('groupName', 'Heading')}
        {field('area', label('area', 'Area'))}
        {field('channel', label('channel', 'Channel'))}
        {field('title', label('title', 'Item'), true)}
        {field('steps', label('steps', 'What to verify / test'), true)}
        {field('testerGroup', label('testerGroup', 'Owner / tester group'))}
        {section?.audience === 'risk' ? field('riskRole', label('riskRole', 'Risk Mgmt Role')) : field('accountTypes', label('accountTypes', 'Account types'))}
        {field('assignedTo', label('assignedTo', 'Assigned to'))}
        <label className="po-field">
          <span>{label('dueDate', 'Due date')}</span>
          <input type="date" value={draft.dueDate} onChange={(e) => setDraft((d) => ({ ...d, dueDate: e.target.value }))} />
        </label>
        {field('evidenceRef', label('evidenceRef', 'Evidence / workpaper ref'))}
        {field('notes', label('notes', 'Notes'), true)}
      </div>
      {item.details.length > 0 && (
        <dl className="po-kv">
          {item.details.filter((d) => d.value).map((d) => (
            <div key={d.label} className="po-kv__row"><dt>{d.label}</dt><dd>{d.value}</dd></div>
          ))}
        </dl>
      )}
      <h3 className="po-h3">Changes</h3>
      {history === null ? <p className="po-muted">Loading…</p> : history.length === 0 ? <p className="po-muted">No changes recorded.</p> : (
        <ul className="po-history">
          {history.map((h) => (
            <li key={h.id}>
              {historyWords(h)}
              <div className="po-muted">{when(h.at)} · {nameOf(h.byUpn, h.byName)} · {h.via === 'rop' ? 'in ROP' : h.via === 'risk' ? 'in the Risk panel' : 'workbook'}</div>
            </li>
          ))}
        </ul>
      )}
    </ModalShell>
  );
}

// ── section editor ───────────────────────────────────────────────────────────────────────────────────────────

function SectionEditor({ section, onClose, onSaved }: { section: PilotSection | 'new'; onClose: () => void; onSaved: () => void }): JSX.Element {
  const isNew = section === 'new';
  const [f, setF] = useState<PilotSectionInput>(() => isNew
    ? { name: '', kind: 'checklist', audience: 'risk', phase: '1', phaseNote: '', description: '', testersMayAdd: false }
    : { name: section.name, audience: section.audience, phase: section.phase, phaseNote: section.phaseNote ?? '', description: section.description ?? '', testersMayAdd: section.testersMayAdd });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(extra: Partial<PilotSectionInput> = {}): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (isNew) await addPilotSection({ ...f, ...extra });
      else await updatePilotSection(section.id, { ...f, ...extra });
      onSaved();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell open title={isNew ? 'Add a section' : `Section: ${section.name}`} onClose={onClose} width={560}
      actions={(
        <>
          {error && <span className="po-error" role="alert">{error}</span>}
          {!isNew && (
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void save({ retired: !section.retired })}>
              {section.retired ? 'Restore section' : 'Retire section'}
            </button>
          )}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--primary" disabled={busy || !f.name?.trim()} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}>
      <div className="po-form">
        <label className="po-field po-field--wide"><span>Name</span>
          <input value={f.name ?? ''} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></label>
        {isNew && (
          <label className="po-field"><span>Kind</span>
            <select value={f.kind} onChange={(e) => setF((x) => ({ ...x, kind: e.target.value as PilotSectionInput['kind'] }))}>
              <option value="checklist">Checklist (status per item)</option>
              <option value="scenarios">Scenarios (pass / fail)</option>
            </select></label>
        )}
        <label className="po-field"><span>Who sees it</span>
          <select value={f.audience} onChange={(e) => setF((x) => ({ ...x, audience: e.target.value as 'testers' | 'risk' }))}>
            <option value="risk">Risk Management only</option>
            <option value="testers">Testers in ROP too</option>
          </select></label>
        <label className="po-field"><span>Phase</span>
          <select value={f.phase} onChange={(e) => setF((x) => ({ ...x, phase: e.target.value }))}>
            <option value="1">Phase 1 (consumer pilot)</option><option value="1.5">Phase 1.5</option><option value="2">Phase 2</option>
          </select></label>
        <label className="po-field"><span>Phase note</span>
          <input value={f.phaseNote ?? ''} placeholder="e.g. Phase 2 — not in the consumer pilot" onChange={(e) => setF((x) => ({ ...x, phaseNote: e.target.value }))} /></label>
        <label className="po-field po-field--wide"><span>Description</span>
          <textarea rows={2} value={f.description ?? ''} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} /></label>
        <label className="po-check">
          <input type="checkbox" checked={!!f.testersMayAdd} onChange={(e) => setF((x) => ({ ...x, testersMayAdd: e.target.checked }))} />
          Testers may add items
        </label>
      </div>
    </ModalShell>
  );
}

// ── add item ─────────────────────────────────────────────────────────────────────────────────────────────────

function AddItem({ section, onClose, onSaved }: { section: PilotSection; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [f, setF] = useState<Record<string, string>>({ itemCode: '', area: '', title: '', steps: '', channel: 'Both', testerGroup: '', riskRole: '', accountTypes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = (k: string, text: string, multi = false): JSX.Element => (
    <label className={`po-field${multi ? ' po-field--wide' : ''}`}><span>{text}</span>
      {multi ? <textarea rows={3} value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} />
        : <input value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} />}
    </label>
  );
  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try { await addPilotItem(section.id, f); onSaved(); } catch (e) { setError(msg(e)); } finally { setBusy(false); }
  }
  return (
    <ModalShell open title={`Add an item to ${section.name}`} onClose={onClose} width={640}
      actions={(
        <>
          {error && <span className="po-error" role="alert">{error}</span>}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--primary" disabled={busy || !(f.title.trim() || f.steps.trim())} onClick={() => void save()}>{busy ? 'Adding…' : 'Add item'}</button>
        </>
      )}>
      <div className="po-form">
        {input('itemCode', 'ID (blank = next in the section)')}
        {input('area', 'Area')}
        {input('title', section.columnLabels.title || 'Item', true)}
        {input('steps', section.columnLabels.steps || 'What to verify / test', true)}
        {input('channel', 'Channel')}
        {input('testerGroup', section.columnLabels.testerGroup || 'Owner / tester group')}
        {section.audience === 'risk' ? input('riskRole', section.columnLabels.riskRole || 'Risk Mgmt Role') : input('accountTypes', section.columnLabels.accountTypes || 'Account types')}
      </div>
    </ModalShell>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────────

export function PilotOversightScreen(): JSX.Element {
  const [view, setView] = useState<View>('items');
  const [board, setBoard] = useState<PilotBoard | null>(null);
  const [changes, setChanges] = useState<PilotHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [f, setF] = useState<OversightFilter>(EMPTY_FILTER);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<number | null>(null);
  const [sectionEdit, setSectionEdit] = useState<PilotSection | 'new' | null>(null);
  const [adding, setAdding] = useState<PilotSection | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setBoard(await loadPilotBoard(showRetired));
      setError(null);
    } catch (e) {
      setError(msg(e));
    }
  }, [showRetired]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    if (view !== 'changes') return;
    setChanges(null);
    loadPilotHistory(300).then((r) => setChanges(r.history)).catch((e) => { setError(msg(e)); setChanges([]); });
  }, [view, board]);

  const sections = board?.sections ?? [];
  const byId = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);
  const shown = useMemo(() => (board ? filterItems(board.items, sections, f) : []), [board, sections, f]);
  const total = totals(sections);
  const searching = !!(f.q || f.status || f.sectionId !== 'all' || f.audience !== 'all');
  const item = editing == null ? null : board?.items.find((i) => i.id === editing) ?? null;
  const done = (): void => { setEditing(null); setSectionEdit(null); setAdding(null); void reload(); };

  return (
    <div className="po-page">
      <PageHeader
        title="Pilot oversight"
        intro={(
          <>
            The pilot test plan, worked in the Retail Operations app instead of a passed-around workbook. Testers update
            the UAT sections, scenarios and results in ROP; Risk Management&rsquo;s own sections (pre-launch, control testing,
            post-implementation) are only here. Every change is recorded with who made it and when.
          </>
        )}
        actions={<button type="button" className="btn btn--primary" onClick={() => setSectionEdit('new')}>Add a section</button>}
      />
      {error && <div className="banner banner--err" role="alert">{error}</div>}

      <FilterChips label="Show" value={view} onChange={(v) => setView(v as View)}
        options={[{ value: 'items', label: 'Items' }, { value: 'changes', label: 'Recent changes' }]} />

      {!board && !error && <p className="empty">Loading…</p>}

      {board && (
        <div className="po-summary-wrap">
          <table className="po-summary">
            <caption>Status summary (as the workbook&rsquo;s Summary tab; % complete leaves N/A out)</caption>
            <thead>
              <tr><th>Section</th><th>Not started</th><th>In progress</th><th>Complete</th><th>Issue identified</th><th>N/A</th><th>Total</th><th>% complete</th></tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={s.id} className={s.retired ? 'is-retired' : undefined}>
                  <td>
                    <button type="button" className="po-link" onClick={() => setSectionEdit(s)}>{s.name}</button>
                    <span className="po-muted"> · {s.audience === 'risk' ? 'Risk only' : 'testers'}{s.phase !== '1' ? ` · Phase ${s.phase}` : ''}{s.retired ? ' · retired' : ''}</span>
                  </td>
                  {s.kind === 'checklist' ? (
                    <>
                      <td>{s.counts.notStarted}</td><td>{s.counts.inProgress}</td><td>{s.counts.complete}</td>
                      <td>{s.counts.issueIdentified}</td><td>{s.counts.notApplicable}</td><td>{s.counts.total}</td><td>{s.counts.percentComplete}%</td>
                    </>
                  ) : <td colSpan={7} className="po-left">{s.counts.total} rows · {s.counts.pass} pass · {s.counts.fail} fail</td>}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total (checklists)</td><td>{total.notStarted}</td><td>{total.inProgress}</td><td>{total.complete}</td>
                <td>{total.issueIdentified}</td><td>{total.notApplicable}</td><td>{total.total}</td><td>{total.percentComplete}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {board && view === 'items' && (
        <>
          <div className="po-filters" role="search">
            <label className="po-field po-field--grow"><span>Search</span>
              <input type="search" value={f.q} placeholder="ID, words, CHG number, owner" onChange={(e) => setF((x) => ({ ...x, q: e.target.value }))} /></label>
            <label className="po-field"><span>Section</span>
              <select value={String(f.sectionId)} onChange={(e) => setF((x) => ({ ...x, sectionId: e.target.value === 'all' ? 'all' : Number(e.target.value) }))}>
                <option value="all">All sections</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label className="po-field"><span>Who works it</span>
              <select value={f.audience} onChange={(e) => setF((x) => ({ ...x, audience: e.target.value as OversightFilter['audience'] }))}>
                <option value="all">Everyone</option><option value="testers">Testers (ROP)</option><option value="risk">Risk only</option>
              </select></label>
            <label className="po-field"><span>Status</span>
              <select value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value }))}>
                <option value="">Any status</option>
                {board.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="Pass">Pass</option><option value="Fail">Fail</option><option value="(none)">No status yet</option>
              </select></label>
            <label className="po-check">
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired
            </label>
          </div>

          {sections.filter((s) => f.sectionId === 'all' || s.id === f.sectionId).map((s) => {
            const rows = shown.filter((i) => i.sectionId === s.id);
            if (searching && rows.length === 0) return null;
            const expanded = open[s.id] ?? searching;
            return (
              <section key={s.id} className={`po-section${expanded ? ' is-open' : ''}`}>
                <div className="po-section__head">
                  <button type="button" className="po-section__toggle" aria-expanded={expanded} onClick={() => setOpen((o) => ({ ...o, [s.id]: !expanded }))}>
                    <span aria-hidden>{expanded ? '▾' : '▸'}</span> {s.name}
                  </button>
                  {s.audience === 'risk' && <StatusChip tone="watch">Risk only</StatusChip>}
                  {s.phaseNote && <StatusChip tone="neutral">{s.phaseNote}</StatusChip>}
                  <span className="po-muted po-section__counts">
                    {s.kind === 'checklist' ? `${s.counts.complete} of ${s.counts.total} complete` : `${s.counts.total} rows`}
                    {searching ? ` · ${rows.length} shown` : ''}
                  </span>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAdding(s)}>Add item</button>
                </div>
                {expanded && (
                  rows.length === 0 ? <p className="po-muted po-pad">No items.</p> : (
                    <ul className="po-list">
                      {rows.map((i) => (
                        <li key={i.id} className={i.retired ? 'is-retired' : undefined}>
                          <button type="button" className="po-row" onClick={() => setEditing(i.id)}>
                            <span className="po-row__key">{i.code ?? '(no ID)'}</span>
                            <span className="po-row__main">
                              <span className="po-row__title">{headline(i, s)}</span>
                              <span className="po-muted">
                                {[i.group, i.area, i.testerGroup, i.riskRole].filter(Boolean).join(' · ')}
                                {i.assignedTo ? ` · ${i.assignedTo}` : ''}
                                {i.updatedAt ? ` · updated ${when(i.updatedAt)} by ${nameOf(i.updatedBy)}` : ''}
                                {i.issues.length ? ` · ${i.issues.map((x) => x.changeKey ?? 'report sent').join(', ')}` : ''}
                              </span>
                            </span>
                            <span className="po-row__chips">
                              {i.result && <StatusChip tone={i.result === 'Fail' ? 'problem' : 'ok'}>{i.result}</StatusChip>}
                              {(i.status || s.kind === 'checklist') && <StatusChip tone={statusTone(i.status)}>{i.status ?? 'Not Started'}</StatusChip>}
                              {i.retired && <StatusChip tone="neutral">Retired</StatusChip>}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </section>
            );
          })}
        </>
      )}

      {board && view === 'changes' && (
        changes === null ? <p className="empty">Loading…</p> : changes.length === 0 ? <EmptyState title="No changes recorded yet." icon="history" /> : (
          <ul className="po-history po-history--page" aria-label="Recent changes">
            {changes.map((h) => (
              <li key={h.id}>
                <strong>{h.itemCode ?? h.sectionName ?? ''}</strong>{h.itemTitle ? ` ${h.itemTitle}` : ''}{h.itemCode && h.sectionName ? <span className="po-muted"> · {h.sectionName}</span> : null}
                <div>{historyWords(h)}</div>
                <div className="po-muted">{when(h.at)} · {nameOf(h.byUpn, h.byName)} · {h.via === 'rop' ? 'in ROP' : h.via === 'risk' ? 'in the Risk panel' : 'workbook'}</div>
              </li>
            ))}
          </ul>
        )
      )}

      {item && board && (
        <ItemEditor item={item} section={byId.get(item.sectionId)} statuses={board.statuses} onClose={() => setEditing(null)} onSaved={done} />
      )}
      {sectionEdit && <SectionEditor section={sectionEdit} onClose={() => setSectionEdit(null)} onSaved={done} />}
      {adding && <AddItem section={adding} onClose={() => setAdding(null)} onSaved={done} />}
    </div>
  );
}
